import { RefObject, useEffect, useRef } from "react";

/** Closes a portaled panel on page/ancestor scroll; the panel's own list stays scrollable. */
export const useCloseOnScroll = (
  open: boolean,
  close: () => void,
  // Only `parentElement` is read, so any Element works.
  anchorRef: RefObject<Element | null>,
  /**
   * The panel itself. Scrolling it must never dismiss it — neither its own list, nor an ancestor
   * the wheel chained into once that list hit its end (MER-8627).
   */
  panelRef?: RefObject<Element | null>
) => {
  // In a ref so re-renders don't reinstall the listeners and race with opening.
  const closeRef = useRef(close);
  closeRef.current = close;

  useEffect(() => {
    if (!open) return;

    const baselines = new Map<HTMLElement | Window, { x: number; y: number }>();
    baselines.set(window, { x: window.scrollX, y: window.scrollY });

    const handleScroll = (event: Event) => {
      const target = event.target;
      const panel = panelRef?.current;

      // Hovered means the pointer is over the dropdown, so the wheel was meant for it — whatever
      // ended up moving. Baselines are still refreshed below, or the next scroll would compare
      // against a stale one and dismiss for nothing.
      const isFromPanel =
        !!panel && ((target instanceof Node && panel.contains(target)) || panel.matches(":hover"));

      // Page scroll: `document` (and jsdom's own target) are not elements.
      if (!(target instanceof Element)) {
        const last = baselines.get(window)!;
        const moved = window.scrollX !== last.x || window.scrollY !== last.y;

        baselines.set(window, { x: window.scrollX, y: window.scrollY });
        if (moved && !isFromPanel) closeRef.current();
        return;
      }

      if (!(target instanceof HTMLElement)) return;

      const last = baselines.get(target);
      // Untracked — e.g. the panel's own list.
      if (!last) return;

      const moved = target.scrollLeft !== last.x || target.scrollTop !== last.y;

      baselines.set(target, { x: target.scrollLeft, y: target.scrollTop });
      if (moved && !isFromPanel) closeRef.current();
    };

    window.addEventListener("scroll", handleScroll, { capture: true });

    const trackedAncestors: HTMLElement[] = [];
    let ancestor = anchorRef.current?.parentElement;

    while (ancestor) {
      const scrollable =
        ancestor.scrollWidth > ancestor.clientWidth ||
        ancestor.scrollHeight > ancestor.clientHeight;

      if (scrollable) {
        baselines.set(ancestor, { x: ancestor.scrollLeft, y: ancestor.scrollTop });
        ancestor.addEventListener("scroll", handleScroll, { capture: true });
        trackedAncestors.push(ancestor);
      }

      ancestor = ancestor.parentElement;
    }

    return () => {
      window.removeEventListener("scroll", handleScroll, { capture: true });
      trackedAncestors.forEach((element) =>
        element.removeEventListener("scroll", handleScroll, { capture: true })
      );
    };
  }, [open, anchorRef, panelRef]);
};
